import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChat } from './useChat';
import { useCurrentUserRole } from 'shared/session';
import { useCurrentUserAccess } from 'shared/session/useCurrentUserRole';
import { useAuth } from 'app/providers/AuthProvider';
import { agentName } from 'shared/branding';
import { SandboxSessionProvider, useSandboxSession } from 'features/sandbox/SandboxSessionContext';
import { extractSandboxUiActionFromToolResult } from 'features/sandbox/utils/sandboxPreviewFromToolResult';
import { ONTOLOGY_VIEWS_QUERY_KEY, SANDBOX_QUERY_KEY } from 'features/views/hooks/useOntologyViews';
import { agentKeys, useAgentChats, useAgentFeed, useDeleteAgentChat, useInbox, useTeamAgents } from 'features/agents/hooks';
import { needsYouCountOf } from 'features/agents/needsYouCount';
import { useConversationTraceExport } from './useConversationTraceExport';
import CredentialSetupModal from './credentials/CredentialSetupModal';
import {
  extractCredentialSetupFromToolResult,
  latestCredentialSetupFromMessages,
} from './credentials/credentialSetupFromToolResult';
import { carriesNeedsYou } from './needsYouFromToolResult';

/**
 * The agent panel: chats with every agent on the team, like ChatGPT.
 *
 * The panel remembers each person's selected session per agent. Authorized
 * interactive and initiative sessions share history and observation; closing
 * the panel or switching sessions never controls execution.
 */
const AgentPanelContext = createContext(null);

// showWork: whether chats show how the agent works (its steps, thinking, tools and
// context use). A person's own choice in this browser; off, a chat is just the conversation.
const STORAGE = {
  agent: 'lineside.agentPanel.agent',
  open: 'lineside.agentPanel.open',
  chats: 'lineside.agentPanel.chats',
  showWork: 'lineside.agentPanel.showWork',
};

const readStorage = (key, fallback = null) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null || value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // Storage is a convenience; the panel works without it.
  }
};

/** The chat that was open with each agent: { [agentId]: sessionId }. */
const readOpenChats = (scopeKey) => {
  try {
    const value = JSON.parse(readStorage(`${STORAGE.chats}:${scopeKey}`, '{}'));
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
};

const rememberOpenChat = (scopeKey, agentId, sessionId) => {
  if (!agentId) return;
  const next = { ...readOpenChats(scopeKey) };
  if (sessionId) next[agentId] = String(sessionId);
  else delete next[agentId];
  writeStorage(`${STORAGE.chats}:${scopeKey}`, JSON.stringify(next));
};

/** Home and Needs you, so what waits on the person never trails the chat. */
const refreshNeedsYou = (queryClient) => {
  void queryClient.invalidateQueries({ queryKey: ['home'] });
  void queryClient.invalidateQueries({ queryKey: agentKeys.inbox() });
  void queryClient.invalidateQueries({ queryKey: agentKeys.feed() });
};

const invalidateSandboxPreviewQueries = (queryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [SANDBOX_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [ONTOLOGY_VIEWS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: ['resolvedViewDataBatch'] }),
    queryClient.invalidateQueries({ queryKey: ['resolvedViewDataSource'] }),
    queryClient.invalidateQueries({ queryKey: ['ontologyViewRuntime'] }),
  ]);

function AgentPanelCore({ children, scopeKey, activePage, agents, agentsLoaded, agent, lead, selectAgent, isAdmin, canManage }) {
  const agentId = agent?.id ? String(agent.id) : null;
  const [isPanelOpen, setIsPanelOpenState] = useState(() => readStorage(STORAGE.open) === 'true');
  const [draftMessage, setDraftMessage] = useState('');
  const [showWork, setShowWorkState] = useState(() => readStorage(STORAGE.showWork) === 'true');
  const setShowWork = useCallback((next) => {
    setShowWorkState(Boolean(next));
    writeStorage(STORAGE.showWork, String(Boolean(next)));
  }, []);
  const [composerRevision, setComposerRevision] = useState(0);
  const [interactionContext, setInteractionContextState] = useState(null);
  const [pageContext, setPageContextState] = useState(null);
  const [composerFocusRequest, setComposerFocusRequest] = useState(0);
  const [credentialSetup, setCredentialSetup] = useState(null);
  const [dismissedCredentialSetup, setDismissedCredentialSetup] = useState(null);
  const [pendingCredentialContinuation, setPendingCredentialContinuation] = useState(null);
  const queryClient = useQueryClient();
  const { selectPreviewSandbox, clearPreview, clearPreviewHints, setActiveChatSessionId } = useSandboxSession();
  const previewChatRef = useRef(null);

  const handleToolComplete = useCallback(
    async ({ toolName, result, sessionId: toolSessionId } = {}) => {
      const originatingChat = toolSessionId || previewChatRef.current;
      if (originatingChat && originatingChat !== previewChatRef.current) return;
      const credentialAction = extractCredentialSetupFromToolResult(toolName, result);
      if (credentialAction) setCredentialSetup(credentialAction);
      if (/initiative|agent/.test(String(toolName || ''))) {
        void queryClient.invalidateQueries({ queryKey: ['agents'] });
        void queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      }
      if (carriesNeedsYou(result)) refreshNeedsYou(queryClient);
      const action = extractSandboxUiActionFromToolResult(toolName, result);
      if (!action) return;
      if (action.type === 'activate' && action.preview?.sandbox_id) {
        // The backend must authorize the new branch/revision before it enters
        // the shared preview. A late receipt cannot switch another chat's lens.
        await invalidateSandboxPreviewQueries(queryClient);
        if (originatingChat === previewChatRef.current) selectPreviewSandbox(action.preview.sandbox_id, action.preview);
        return;
      }
      if (action.type === 'clear') {
        clearPreview();
        void invalidateSandboxPreviewQueries(queryClient);
      }
      if (action.type === 'refresh') {
        // A staged execution/reset can move the effective revision without
        // returning preview metadata. Let the refreshed list resolve it.
        clearPreviewHints();
        void invalidateSandboxPreviewQueries(queryClient);
      }
    },
    [queryClient, selectPreviewSandbox, clearPreview, clearPreviewHints]
  );

  const chat = useChat({
    usageType: 'general_agent_message_context',
    activePage,
    onToolComplete: handleToolComplete,
    agentId,
    observe: isPanelOpen,
  });
  const {
    messages,
    sendMessage,
    sessionId,
    sessionAgentId,
    runId,
    isStreaming,
    createNewSession,
    resumeSession,
  } = chat;

  useLayoutEffect(() => {
    const current = sessionAgentId === agentId ? sessionId : null;
    previewChatRef.current = current;
    setActiveChatSessionId?.(current);
  }, [sessionId, sessionAgentId, agentId, setActiveChatSessionId]);

  // The agent the chat on screen belongs to. The open chat is remembered per
  // agent only once it is known to be that agent's (never mid-switch).
  const chatAgentRef = useRef(null);
  const [chatError, setChatError] = useState(null);
  const openRequestRef = useRef(0);

  const resetComposer = useCallback(() => {
    setComposerRevision((revision) => revision + 1);
    setDraftMessage('');
    setCredentialSetup(null);
    setDismissedCredentialSetup(null);
  }, []);

  /** A fresh private interactive chat with the selected agent. */
  const startNewChat = useCallback(() => {
    openRequestRef.current += 1;
    previewChatRef.current = null;
    setActiveChatSessionId?.(null);
    resetComposer();
    setChatError(null);
    chatAgentRef.current = agentId;
    rememberOpenChat(scopeKey, agentId, null);
    createNewSession();
  }, [scopeKey, agentId, createNewSession, resetComposer, setActiveChatSessionId]);

  /** Reopen one of the person's earlier chats with the selected agent. */
  const openChat = useCallback(async (targetSessionId) => {
    if (!targetSessionId) return false;
    const requestId = ++openRequestRef.current;
    previewChatRef.current = null;
    setActiveChatSessionId?.(null);
    resetComposer();
    setChatError(null);
    chatAgentRef.current = agentId;
    rememberOpenChat(scopeKey, agentId, targetSessionId);
    const opened = await resumeSession(String(targetSessionId));
    if (requestId !== openRequestRef.current) return false;
    if (!opened) {
      rememberOpenChat(scopeKey, agentId, null);
      setChatError('That chat could not be opened.');
    }
    return opened;
  }, [scopeKey, agentId, resumeSession, resetComposer, setActiveChatSessionId]);

  // Switching agents reopens the chat that was open with that agent, or starts a new one.
  const openedForAgentRef = useRef(null);
  const pendingSessionRef = useRef(null);
  useEffect(() => {
    if (!agentId || !chat.isReady || openedForAgentRef.current === agentId) return;
    openedForAgentRef.current = agentId;
    const pending = pendingSessionRef.current;
    if (pending?.agentId === agentId) {
      pendingSessionRef.current = null;
      void openChat(pending.sessionId).then((opened) => {
        if (opened && pending.focus) setComposerFocusRequest((request) => request + 1);
        pending.resolve(opened);
      });
      return;
    }
    const remembered = readOpenChats(scopeKey)[agentId];
    if (remembered) {
      void openChat(remembered).then((opened) => {
        if (!opened) {
          setChatError(null);
          startNewChat();
        }
      });
    } else {
      startNewChat();
    }
  }, [scopeKey, agentId, chat.isReady, openChat, startNewChat]);

  // Text another page put in the composer for an agent, applied once that agent's chat is open.
  const [pendingDraft, setPendingDraft] = useState(null);
  useEffect(() => {
    if (!pendingDraft || pendingDraft.agentId !== agentId || !chat.isReady || openedForAgentRef.current !== agentId) return;
    setDraftMessage(pendingDraft.text);
    setComposerFocusRequest((request) => request + 1);
    setPendingDraft(null);
  }, [pendingDraft, agentId, chat.isReady]);

  // A new chat gets its id with the first reply; remember it as this agent's open chat.
  useEffect(() => {
    if (sessionId && agentId && chatAgentRef.current === agentId) rememberOpenChat(scopeKey, agentId, sessionId);
  }, [scopeKey, sessionId, agentId]);

  const chats = useAgentChats(agentId, { enabled: Boolean(agentId), scopeKey, poll: isPanelOpen });
  // The list is not read on a timer while the panel is closed: read it as the panel opens.
  const wasPanelOpenRef = useRef(isPanelOpen);
  useEffect(() => {
    const opened = isPanelOpen && !wasPanelOpenRef.current;
    wasPanelOpenRef.current = isPanelOpen;
    if (opened && agentId) void queryClient.invalidateQueries({ queryKey: agentKeys.chats(agentId) });
  }, [isPanelOpen, agentId, queryClient]);
  useEffect(() => {
    if (chat.connectionState !== 'forbidden') return;
    rememberOpenChat(scopeKey, agentId, null);
    void queryClient.invalidateQueries({ queryKey: agentKeys.chats(agentId) });
  }, [chat.connectionState, scopeKey, agentId, queryClient]);
  const deleteChatMutation = useDeleteAgentChat(agentId);
  const deleteChat = useCallback(async (targetSessionId) => {
    await deleteChatMutation.mutateAsync(targetSessionId);
    if (String(targetSessionId) === String(sessionId)) startNewChat();
  }, [deleteChatMutation, sessionId, startNewChat]);

  // A finished reply can rename a new chat, its briefing may have told the person
  // about updates, and its turn may have raised or settled what waits on them:
  // refresh the chat list, the updates, Home and Needs you.
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      void queryClient.invalidateQueries({ queryKey: agentKeys.chats(agentId) });
      void queryClient.invalidateQueries({ queryKey: ['agent-activity'] });
      void queryClient.invalidateQueries({ queryKey: agentKeys.updates() });
      refreshNeedsYou(queryClient);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, agentId, queryClient]);

  const feed = useAgentFeed({ enabled: agents.length > 0 });
  // The bubble's badge counts the same Needs you list as the sidebar and Home. The lighter feed
  // polls more often, so a change in its count refreshes that list for every surface at once.
  const inbox = useInbox();
  const feedNeedsYou = feed.data?.needs_you;
  const lastFeedNeedsYou = useRef(feedNeedsYou);
  useEffect(() => {
    const previous = lastFeedNeedsYou.current;
    lastFeedNeedsYou.current = feedNeedsYou;
    if (previous !== undefined && feedNeedsYou !== undefined && previous !== feedNeedsYou) {
      void queryClient.invalidateQueries({ queryKey: agentKeys.inbox() });
    }
  }, [feedNeedsYou, queryClient]);
  // A dot on an agent that has updates the person has not seen yet.
  const unreadAgentIds = useMemo(() => {
    const unread = new Set();
    Object.entries(feed.data?.agents || {}).forEach(([id, entry]) => {
      if (Number(entry?.unseen_updates || 0) > 0) unread.add(String(id));
    });
    return unread;
  }, [feed.data]);

  const setIsPanelOpen = useCallback((next) => {
    setIsPanelOpenState((current) => {
      const value = typeof next === 'function' ? next(current) : Boolean(next);
      writeStorage(STORAGE.open, value);
      return value;
    });
  }, []);

  /** Open the panel on the agent in front of the person. */
  const showConversation = useCallback(() => {
    setIsPanelOpen(true);
  }, [setIsPanelOpen]);

  /** Open the panel on an agent; `newChat` starts a fresh chat with it. */
  const openAgent = useCallback((targetAgentId, { focus = false, newChat = false } = {}) => {
    const target = targetAgentId ? String(targetAgentId) : agentId;
    if (newChat && target) {
      if (target === agentId) startNewChat();
      else rememberOpenChat(scopeKey, target, null);
    }
    if (targetAgentId) selectAgent(target);
    showConversation();
    if (focus) setComposerFocusRequest((request) => request + 1);
  }, [scopeKey, agentId, selectAgent, showConversation, startNewChat]);

  /** Exact authorized activity navigation; never substitute the remembered chat. */
  const openSession = useCallback((targetAgentId, targetSessionId, { focus = false } = {}) => {
    const target = String(targetAgentId || '');
    if (!targetSessionId || !agents.some((item) => String(item.id) === target)) return Promise.resolve(false);
    showConversation();
    if (target === agentId) return openChat(String(targetSessionId)).then((opened) => {
      if (opened && focus) setComposerFocusRequest((request) => request + 1);
      return opened;
    });
    pendingSessionRef.current?.resolve(false);
    return new Promise((resolve) => {
      pendingSessionRef.current = { agentId: target, sessionId: String(targetSessionId), focus, resolve };
      selectAgent(target);
    });
  }, [agentId, agents, openChat, selectAgent, showConversation]);

  const setInteractionContext = useCallback((nextContext) => {
    if (!nextContext?.source || !nextContext?.label) {
      setInteractionContextState(null);
      return;
    }
    setInteractionContextState({
      source: String(nextContext.source),
      label: String(nextContext.label),
      context: nextContext.context && typeof nextContext.context === 'object' ? nextContext.context : {},
    });
  }, []);

  const clearInteractionContext = useCallback((source = null) => {
    setInteractionContextState((current) => {
      if (!current) return null;
      if (source && current.source !== source) return current;
      return null;
    });
  }, []);

  // Pages publish what the person is looking at; it rides along with each message.
  const publishPageContext = useCallback((next) => {
    setPageContextState(next && typeof next === 'object' ? next : null);
  }, []);

  const requestComposerFocus = useCallback(() => setComposerFocusRequest((request) => request + 1), []);

  /** Start a message to an agent without sending it, in whichever chat with it is open. */
  const prefillComposer = useCallback((text, targetAgentId = null) => {
    const target = targetAgentId ? String(targetAgentId) : agentId;
    if (!target) return;
    if (target !== agentId) selectAgent(target);
    setPendingDraft({ agentId: target, text: String(text || '') });
  }, [agentId, selectAgent]);

  const focusChatComposer = useCallback(() => {
    showConversation();
    requestComposerFocus();
  }, [showConversation, requestComposerFocus]);

  const buildContext = useCallback(
    (extraContext = {}) => ({
      page: activePage || undefined,
      page_context: { page: activePage || undefined, ...(pageContext || {}) },
      ...(interactionContext?.context || {}),
      ...(extraContext && typeof extraContext === 'object' ? extraContext : {}),
    }),
    [activePage, pageContext, interactionContext]
  );

  const sendMessageWithContext = useCallback(
    async (message, extraContext = {}, options = {}) => {
      const result = await sendMessage(message, buildContext(extraContext), options);
      if (result?.success) {
        void queryClient.invalidateQueries({ queryKey: ['agent-activity'] });
        void queryClient.invalidateQueries({ queryKey: agentKeys.chats(agentId) });
      }
      return result;
    },
    [sendMessage, buildContext, queryClient, agentId]
  );

  // Ask or assign work from anywhere; it goes to the chat that is open with the agent.
  const openChatAndSendMessage = useCallback(
    (message, extraContext = {}) => {
      if (!message?.trim()) return;
      showConversation();
      void sendMessageWithContext(message.trim(), extraContext);
    },
    [sendMessageWithContext, showConversation]
  );

  const closeCredentialSetup = useCallback(() => {
    setCredentialSetup((current) => {
      if (current) setDismissedCredentialSetup(current);
      return null;
    });
  }, []);

  const openCredentialSetup = useCallback((action) => {
    if (!action?.requestId || !action?.connectionId) return;
    setDismissedCredentialSetup(null);
    setCredentialSetup(action);
  }, []);

  const handleCredentialSetupCompleted = useCallback((result) => {
    setCredentialSetup(null);
    setDismissedCredentialSetup(null);
    setPendingCredentialContinuation({
      requestId: result?.request_id || null,
      connectionId: result?.connection_id || null,
      connectionName: result?.connection_name || null,
    });
  }, []);

  useEffect(() => {
    if (isStreaming || !pendingCredentialContinuation?.connectionId) return;
    const event = pendingCredentialContinuation;
    setPendingCredentialContinuation(null);
    sendMessageWithContext(
      `Credential setup completed through the secure broker for integration connection ${event.connectionId}. Check credential status, test the connection, and continue the integration workflow.`,
      { integrationCredentialEvent: { type: 'credential_setup_completed', requestId: event.requestId, connectionId: event.connectionId } }
    );
  }, [isStreaming, pendingCredentialContinuation, sendMessageWithContext]);

  useEffect(() => {
    if (credentialSetup || dismissedCredentialSetup) return;
    const recovered = latestCredentialSetupFromMessages(messages);
    if (recovered) setDismissedCredentialSetup(recovered);
  }, [messages, credentialSetup, dismissedCredentialSetup]);

  const chatAgentDisplayName = agentName(agent);

  const { exportConversationTrace, isExportingConversation, exportConversationError } = useConversationTraceExport({
    sessionId,
    runId,
    sessionAgentId,
    messages,
    isStreaming,
    activePage,
    agentDisplayName: chatAgentDisplayName,
  });

  const value = useMemo(
    () => ({
      ...chat,
      sendMessage: sendMessageWithContext,
      // Pages that start or reopen a chat (e.g. "Build here") go through the panel,
      // so it remembers which chat is open with the agent.
      createNewSession: startNewChat,
      resumeSession: openChat,
      startNewChat,
      openChat,
      deleteChat,
      chats: [401, 403, 404].includes(chats.error?.status) ? [] : chats.data || [],
      chatsLoading: chats.isLoading,
      chatError,
      isPanelOpen,
      setIsPanelOpen,
      openAgent,
      openSession,
      selectAgent,
      draftMessage,
      composerRevision,
      setDraftMessage,
      interactionContext,
      setInteractionContext,
      clearInteractionContext,
      pageContext,
      publishPageContext,
      composerFocusRequest,
      requestComposerFocus,
      prefillComposer,
      focusChatComposer,
      openChatAndSendMessage,
      activePage,
      agents,
      // The agent list arrived, even if the person may use none (only superadmins chat with the lead).
      agentsLoaded,
      agent,
      lead,
      agentId,
      chatAgentDisplayName,
      chatAgentDescription: String(agent?.description || '').trim(),
      chatAgentStarterPrompts: agent?.chat_starter_prompts?.length ? agent.chat_starter_prompts : null,
      feed: feed.data || null,
      needsYouCount: inbox.data ? needsYouCountOf(inbox.data) : Number(feed.data?.needs_you || 0),
      unseenUpdatesCount: Number(feed.data?.unseen_updates || 0),
      unreadAgentIds,
      isAdmin,
      canManage,
      exportConversationTrace,
      isExportingConversation,
      exportConversationError,
      openCredentialSetup,
      showWork,
      setShowWork,
    }),
    [
      chat, sendMessageWithContext, startNewChat, openChat, deleteChat, chats.data, chats.isLoading, chats.error,
      chatError, isPanelOpen, setIsPanelOpen, openAgent, openSession, selectAgent, draftMessage, composerRevision, interactionContext,
      setInteractionContext, clearInteractionContext, pageContext, publishPageContext, composerFocusRequest,
      requestComposerFocus, prefillComposer, focusChatComposer, openChatAndSendMessage, activePage, agents, agentsLoaded, agent, lead, agentId, chatAgentDisplayName,
      feed.data, unreadAgentIds, isAdmin, canManage, exportConversationTrace, isExportingConversation,
      exportConversationError, openCredentialSetup, showWork, setShowWork,
    ]
  );

  return (
    <AgentPanelContext.Provider value={value}>
      {children}
      {dismissedCredentialSetup && !credentialSetup ? (
        <div className="fixed bottom-5 right-5 z-[60] max-w-sm rounded-lg border border-emerald-200 bg-white p-3 shadow-xl ring-1 ring-black/5">
          <p className="text-sm font-medium text-neutral-950">Credential setup is still waiting</p>
          <p className="mt-0.5 text-xs text-neutral-600">{dismissedCredentialSetup.connectionName || 'Integration connection'}</p>
          <button
            type="button"
            onClick={() => openCredentialSetup(dismissedCredentialSetup)}
            className="mt-2 h-8 rounded-md bg-neutral-950 px-3 text-xs font-semibold text-white hover:bg-neutral-800"
          >
            Continue setup
          </button>
        </div>
      ) : null}
      <CredentialSetupModal requestAction={credentialSetup} onClose={closeCredentialSetup} onCompleted={handleCredentialSetupCompleted} />
    </AgentPanelContext.Provider>
  );
}

export const AgentPanelProvider = ({ children, activePage = '' }) => {
  const { session: authSession } = useAuth();
  const { data: access } = useCurrentUserAccess();
  const scopeKey = `${authSession?.user?.id || ''}:${access?.organizationId || ''}:${access?.profileId || ''}`;
  const team = useTeamAgents();
  const { data: currentUserRole } = useCurrentUserRole();
  const isAdmin = currentUserRole === 'admin';
  const [selectedAgentId, setSelectedAgentId] = useState(() => readStorage(`${STORAGE.agent}:${scopeKey}`));
  const agents = useMemo(() => team.agents.filter((item) => item.status !== 'retired' && item.runtime_support?.chat !== false && item.runtime_kind !== 'external'), [team.agents]);
  const agent = useMemo(
    () => agents.find((item) => String(item.id) === String(selectedAgentId)) || team.lead || null,
    [agents, selectedAgentId, team.lead]
  );

  const selectAgent = useCallback((nextId) => {
    const value = nextId ? String(nextId) : null;
    setSelectedAgentId(value);
    writeStorage(`${STORAGE.agent}:${scopeKey}`, value);
  }, [scopeKey]);

  useEffect(() => { setSelectedAgentId(readStorage(`${STORAGE.agent}:${scopeKey}`)); }, [scopeKey]);

  return (
    <SandboxSessionProvider key={scopeKey}>
      <AgentPanelCore
        key={scopeKey}
        scopeKey={scopeKey}
        activePage={activePage}
        agents={agents}
        agentsLoaded={team.isFetched}
        agent={agent}
        lead={team.lead}
        selectAgent={selectAgent}
        isAdmin={isAdmin}
        canManage={team.canManage}
      >
        {children}
      </AgentPanelCore>
    </SandboxSessionProvider>
  );
};

export const useOptionalAgentPanel = () => useContext(AgentPanelContext);

export const useAgentPanel = () => {
  const ctx = useOptionalAgentPanel();
  if (!ctx) throw new Error('useAgentPanel must be used within AgentPanelProvider');
  return ctx;
};

/** Pages call this with what the person is looking at (view, record, initiative). */
export const usePublishPageContext = (context) => {
  const panel = useOptionalAgentPanel();
  const publish = panel?.publishPageContext;
  const serialized = context ? JSON.stringify(context) : '';
  useEffect(() => {
    if (!publish) return undefined;
    publish(serialized ? JSON.parse(serialized) : null);
    return () => publish(null);
  }, [publish, serialized]);
};

export default AgentPanelContext;
